// SPDX-License-Identifier: Elastic-2.0
/**
 * HistorySnippet — last-N state changes for a device.
 *
 * Per task spec § 9: if the F-300 OpenAPI exposes
 * `GET /devices/{id}/history` we render the list (relative time + state
 * line); otherwise we ship a stub with the i18n copy "History coming
 * soon" and a `TODO(Lane-A)` marker so the future endpoint can drop in.
 *
 * STATUS at T-024 (2026-05): the OpenAPI snapshot at
 * `docs/api/mirlai-v1-openapi.json` does NOT include a history endpoint
 * (verified by grep — no path or schema named "*history*"). We ship the
 * STUB.
 *
 * Upgrade path: when F-300 adds `getDeviceHistory(id, { limit })` to
 * `DevicesService`, replace `useStubHistory()` with a real
 * `useQuery(["devices", id, "history"], …)` call and render the entries
 * via the existing `<ul>` skeleton. No other component needs to change.
 *
 * TODO(Lane-A, F-300): expose `GET /devices/{device_id}/history?limit=N`
 * returning `{ entries: Array<{ at: string; state: Record<string, any>;
 * summary: string; }> }`. Tracked via task-spec § 9 "Notes".
 */
import { useTranslation } from "react-i18next";

import styles from "./DeviceModal.module.css";

export interface HistorySnippetProps {
  deviceId: string;
  /** Max entries to surface once the endpoint exists. Default 5. */
  limit?: number;
}

export function HistorySnippet({
  deviceId: _deviceId,
  limit: _limit = 5,
}: HistorySnippetProps): JSX.Element {
  const { t } = useTranslation();
  // STUB: no F-300 endpoint yet (see file header). Once the endpoint
  // lands, swap this for a real TanStack query and feed the entries into
  // a `<ul>` of `<li>{relativeTime} — {summary}</li>` rows.
  return (
    <section
      className={styles.historyStub}
      aria-labelledby="history-title"
      data-testid="history-snippet"
    >
      <h3 id="history-title" className={styles.sectionHeading}>
        {t("device_modal.history_title")}
      </h3>
      <p className={styles.historyStubText}>{t("device_modal.history_stub")}</p>
    </section>
  );
}

export default HistorySnippet;
